import { useCallback, useEffect, useState } from "react";

import { Button, Spinner } from "@blueprintjs/core";
import styled from "@emotion/styled";
import uniq from "lodash/uniq";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { pluralize } from "@/components/helpers/string-utils";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { breakpoints } from "@/components/layout/Media";
import { CoachCard } from "@/components/pages_logged_in/program/components/set-components/CoachCard";
import { Paging } from "@/components/pages_logged_in/workspace-admin/Paging";
import { SearchBox } from "@/components/pages_logged_in/workspace-admin/SearchBox";
import { CollapsibleSection } from "@/components/pieces/form/CollapsibleSection";
import { StandaloneCheckbox } from "@/components/pieces/form/StandaloneCheckbox";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { Card, CardContent } from "@/components/ui/card";
import { SUBTEXT_COLOR } from "@/css/constants";
import {
  ComponentTypeEnum,
  FilterCreditInput,
  PaginatedCoachesQuery,
  usePaginatedCoachesLazyQuery,
  useStandardCredentialsQuery,
} from "@/graphql";
import { ComponentTabProps } from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";
import { formatCreditsLowerCase, parseDecimal } from "@/utils/string";
import { NonNullableArrayItem } from "@/utils/types";

type Coach = NonNullableArrayItem<
  NonNullable<NonNullable<PaginatedCoachesQuery["paginatedCoaches"]>["data"]>
>;

interface CoachesListProps {
  coaches: Coach[];
  onSelect: (coachIds: string[]) => void;
  selectedCoachIds: string[];
  loading: boolean;
  productIds: string[];
}

function CoachesList({
  coaches,
  onSelect,
  selectedCoachIds = [],
  loading,
  productIds,
}: CoachesListProps) {
  return (
    <Box css={{ position: "relative" }}>
      {loading && (
        <Box
          css={{
            position: "absolute",
            top: 0,
            textAlign: "center",
            width: "100%",
          }}
        >
          <Box
            css={{
              display: "inline-flex",
              background: "#FFFFFF99",
              padding: 5,
            }}
            fontSize={16}
            fontWeight={500}
            color={SUBTEXT_COLOR}
          >
            <Spinner size={20} style={{ marginRight: 5 }} />
            Loading coaches...
          </Box>
        </Box>
      )}
      {coaches?.length === 0 && !loading && (
        <Card className="text-center text-gray-500">
          <CardContent className="p-4">
            <p className="font-semibold">No results</p>
            <p className="font-normal">Try adjusting search criteria</p>
          </CardContent>
        </Card>
      )}
      {coaches.map((coach, index) => (
        <Box mt={index > 0 ? 24 : 0} key={coach.id}>
          <CoachCard
            coach={coach}
            onChoose={(selected) => {
              if (selected) {
                onSelect(uniq([coach.id, ...selectedCoachIds]));
              } else {
                onSelect(selectedCoachIds.filter((id) => id !== coach.id));
              }
            }}
            isChosen={selectedCoachIds.includes(coach.id)}
            productIds={productIds}
          />
        </Box>
      ))}
    </Box>
  );
}

interface CoachesFilterCheckBoxProps {
  label: React.ReactNode;
  checked: boolean;
  onChange: () => void;
}

function CoachesFilterCheckBox({
  label,
  checked,
  onChange,
}: CoachesFilterCheckBoxProps) {
  return (
    <Flex flexDirection="column">
      <Flex>
        <StandaloneCheckbox onChange={onChange} checked={checked} />
        <Clickable onClick={onChange}>
          <Box style={{ fontSize: 16 }} ml={2}>
            {label}
          </Box>
        </Clickable>
      </Flex>
    </Flex>
  );
}

export function Coaches({
  input,
  programId,
  onNext,
  programComponent,
}: ComponentTabProps) {
  const [fetchCoaches, { data: coachesQueryData, loading: loadingCoach }] =
    usePaginatedCoachesLazyQuery();

  const { data: standardCredentialsQueryData } = useStandardCredentialsQuery();

  const [query, setQuery] = useState("");
  const [selectedCoachIds, setSelectedCoachIds] = useState<string[]>([]);
  const [selectedStandardCredentialIds, setSelectedStandardCredentialIds] =
    useState<string[]>([]);
  const [filterCredits, setFilterCredits] = useState<FilterCreditInput[]>([]);
  const [onlyShowSelected, setOnlyShowSelected] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const coachesEmails = ensureNonNullArray(input.coaches);
    if (coachesEmails.length) {
      setSelectedCoachIds(coachesEmails);
    }
  }, [input.coaches]);

  const pageSize = 5;

  const { paginatedCoaches } = coachesQueryData || {
    paginatedCoaches: {
      data: [],
      currentPage: 1,
      pageSize: 5,
      totalRecords: 0,
    },
  };

  const coachesData = {
    coaches: paginatedCoaches?.data || [],
    page: paginatedCoaches?.currentPage || page,
    pageSize: paginatedCoaches?.pageSize || pageSize,
    totalRecords: paginatedCoaches?.totalRecords || 0,
  };

  const standardCredentials = ensureNonNullArray(
    standardCredentialsQueryData?.standardCredentials,
  );

  const sessionComponents = ensureNonNullArray(
    programComponent?.childComponents,
  ).filter(
    (component) => component.type === ComponentTypeEnum.OneOneCoachingSession,
  );

  const sessionProducts = sessionComponents
    .map((component) => {
      return component?.product;
    })
    .filter((product) => product !== null && product !== undefined);

  const distinctSessionProducts = uniq(sessionProducts);

  const productIds = sessionProducts
    .map((product) => product?.id)
    .filter((productId) => productId !== null);

  useEffect(() => {
    if (programId && productIds.length > 0) {
      const distinctProductIds = uniq(productIds);
      fetchCoaches({
        variables: {
          currentPage: page,
          pageSize: onlyShowSelected ? selectedCoachIds.length : pageSize,
          query: !onlyShowSelected ? query : "",
          programId,
          // Just send productIds, not need to send specialtyIds
          specialtyIds: [],
          productIds: distinctProductIds,
          coachIds: onlyShowSelected ? selectedCoachIds : [],
          credentialIds: !onlyShowSelected ? selectedStandardCredentialIds : [],
          filterCreditInputs: !onlyShowSelected ? filterCredits : [],
        },
      });
    }
  }, [
    query,
    page,
    onlyShowSelected,
    programId,
    selectedStandardCredentialIds,
    programComponent,
    filterCredits,
  ]);

  const onSearch = (query: string) => {
    setQuery(query);
    setPage(1);
  };

  const onChoose = useCallback(() => {
    onNext({
      value: {
        coaches: selectedCoachIds,
      },
    });
  }, [onNext, selectedCoachIds]);

  const onOnlyShowSelectedChange = useCallback(() => {
    setOnlyShowSelected(!onlyShowSelected);
    setPage(1);
  }, [onlyShowSelected]);

  const onStandardCredentialChange = useCallback(
    (credentialId: string) => {
      setSelectedStandardCredentialIds(
        selectedStandardCredentialIds.includes(credentialId)
          ? selectedStandardCredentialIds.filter((id) => id !== credentialId)
          : [...selectedStandardCredentialIds, credentialId],
      );
    },
    [selectedStandardCredentialIds],
  );

  const onFilterCreditsChange = useCallback(
    (productId: string, value: number) => {
      // Check if this credit filter already exists
      const existingCreditIndex = filterCredits.findIndex(
        (credit) =>
          credit.productId === productId && credit.creditPrice === value,
      );

      if (existingCreditIndex >= 0) {
        // If exists, remove it (toggle off)
        const newFilterCredits = [...filterCredits];
        newFilterCredits.splice(existingCreditIndex, 1);
        setFilterCredits(newFilterCredits);
      } else {
        // If doesn't exist, add it (toggle on)
        setFilterCredits([
          ...filterCredits,
          {
            productId,
            creditPrice: value,
          },
        ]);
      }
    },
    [filterCredits],
  );

  const hasCoach = selectedCoachIds?.length > 0;

  const coachTitle = hasCoach ? "Choose coaches" : "Optional: Curate Coaches";
  const coachSubtitle = hasCoach
    ? "Decide which coaches will be available to participants"
    : "Decide which coaches will be available to participants. By default, all coaches are available";

  const coachIncludedText = hasCoach
    ? `${pluralize({
        amount: selectedCoachIds?.length || 0,
        singular: "coach",
        plural: "coaches",
        showCount: true,
      })} included`
    : "Allow All Coaches";

  return (
    <div className="max-w-full gap-6 flex flex-col my-0 mx-auto p-9">
      <div>
        <div className="text-2xl font-semibold">{coachTitle}</div>
        <div className="text-subtext mt-2.5">{coachSubtitle}</div>
      </div>

      <hr className="m-0 border-neutral-300" />

      <div className="flex xs:flex-col lg:flex-row w-full lg:gap-15 xs:gap-5">
        <div className="xs:w-full lg:w-66.25 flex flex-col gap-6">
          <div className="text-lg font-bold">Filters</div>
          <div>
            <CoachesFilterCheckBox
              label="Only show selected coaches"
              checked={onlyShowSelected}
              onChange={onOnlyShowSelectedChange}
            />
          </div>
          <div>
            <CollapsibleSection
              title="Semantic Filter"
              backgroundColor={"transparent"}
              defaultOpen={true}
              p={0}
            >
              <Box flex="1" width={1}>
                <SearchBox
                  style={{
                    borderRadius: 6,
                    border: "0.5px solid #E2E2E2",
                    overflow: "hidden",
                    padding: "22px 0px 22px 36px",
                    fontSize: 14,
                  }}
                  className="icon-center"
                  refine={onSearch}
                  throttledDuration={400}
                  searchPlaceholder="Search Coaches"
                />
              </Box>
            </CollapsibleSection>
          </div>
          <div className="flex xs:flex-col lg:flex-col md:flow-row sm:flex-row w-full gap-6">
            <div className="flex-1 flex flex-col gap-6">
              {distinctSessionProducts.map((product) => {
                const { creditPrice, acceptablePriceIncrement, priceRange } =
                  product || {};
                const { minCreditPrice, maxCreditPrice } = priceRange || {};
                const parsedMinCreditPrice = parseDecimal(minCreditPrice);
                const parsedMaxCreditPrice = parseDecimal(maxCreditPrice);
                const priceIncrement = acceptablePriceIncrement || 0;
                let priceArray = [];

                if (parsedMinCreditPrice < parsedMaxCreditPrice) {
                  priceArray = Array.from(
                    {
                      length:
                        (parsedMaxCreditPrice - parsedMinCreditPrice) /
                          priceIncrement +
                        1,
                    },
                    (_, i) => parsedMinCreditPrice + i * priceIncrement,
                  );
                } else {
                  priceArray.push(parseDecimal(creditPrice));
                }

                return (
                  <CollapsibleSection
                    key={product.id}
                    title={`Price Per ${product.name}`}
                    backgroundColor={"transparent"}
                    defaultOpen={true}
                    p={0}
                  >
                    <div className="flex flex-col gap-2.5 mt-2.5 mx-2.5">
                      {priceArray.slice(0, 10).map((value) => (
                        <CoachesFilterCheckBox
                          key={value}
                          label={
                            <Flex>
                              <EmbedSvg
                                src={CreditSVG}
                                height={18}
                                width={18}
                              />{" "}
                              <Box ml={1}>{formatCreditsLowerCase(value)}</Box>
                            </Flex>
                          }
                          checked={
                            filterCredits?.some(
                              (credit) =>
                                credit.productId === product.id &&
                                credit.creditPrice === value,
                            ) || false
                          }
                          onChange={() =>
                            onFilterCreditsChange(product.id, value)
                          }
                        />
                      ))}
                    </div>
                  </CollapsibleSection>
                );
              })}
            </div>

            <div className="flex-1">
              <CollapsibleSection
                title="Coaching Credentials"
                backgroundColor={"transparent"}
                defaultOpen={true}
                p={0}
              >
                <div className="flex flex-col gap-2.5 mt-2.5 mx-2.5">
                  {standardCredentials.map((credential) => (
                    <CoachesFilterCheckBox
                      key={credential.id}
                      label={credential.name}
                      checked={selectedStandardCredentialIds.includes(
                        credential.id,
                      )}
                      onChange={() => onStandardCredentialChange(credential.id)}
                    />
                  ))}
                </div>
              </CollapsibleSection>
            </div>
          </div>
        </div>

        <div className="flex-1">
          <CoachesList
            loading={loadingCoach}
            coaches={ensureNonNullArray(coachesData?.coaches)}
            onSelect={(coachIds) => setSelectedCoachIds(coachIds)}
            selectedCoachIds={selectedCoachIds}
            productIds={productIds}
          />
          {!onlyShowSelected && coachesData?.totalRecords > 0 && (
            <Box mt={3}>
              <Paging
                totalRecords={coachesData?.totalRecords}
                pageSize={pageSize}
                currentPage={page}
                setCurrentPage={setPage}
              />
            </Box>
          )}
        </div>
      </div>

      <Footer justifyContent="space-between" alignItems="center">
        <Box>{coachIncludedText}</Box>
        <Button intent="primary" large={true} onClick={onChoose}>
          Continue
        </Button>
      </Footer>
    </div>
  );
}

const Footer = styled(Flex)`
  border-top: 1px solid #e5e5e5;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  padding: 10px 36px;

  @media (min-width: ${breakpoints.sm}px) {
    left: 210px;
  }
`;
